/**
 * Fetches active semester and approved classes for the landing page.
 * Never shows static dummy semester/courses while loading or after API resolves.
 */

import { useState, useEffect, useCallback } from 'react';

import { buildActiveSemesterFromApi } from '@constants/semester';
import {
  ACTIVE_SEMESTER_BROADCAST_CHANNEL,
  ACTIVE_SEMESTER_CACHE_MS_NONE,
  ACTIVE_SEMESTER_CHANGED_EVENT,
} from '@constants/activeSemesterSync';
import { getActiveSemester, getPublicClasses } from '@services/publicLandingApi';
import {
  buildClassExcerpt,
  formatClassPriceLabel,
  getScheduledTimeLabel,
} from '@utils/publicClassDisplay';
import {
  clearPublicLandingSemesterCache,
  readPublicLandingSemesterCache,
  writePublicLandingSemesterCache,
} from '@utils/publicLandingCache';

import type { ActiveSemester } from '@constants/semester';
import type { Course, GradeLevel } from '../types/courses';
import type { PublicClassItem } from '@services/publicLandingApi';

const GRADE_LEVEL_MAP: Record<string, GradeLevel> = {
  Preschool: 'Preschool',
  Elementary: 'Elementary',
  'Middle School': 'Middle',
  Middle: 'Middle',
  'High School': 'High School',
  Adult: 'Adult',
};

function mapApiGradeLevel(raw: string | undefined): GradeLevel {
  if (!raw) {
    return 'Elementary';
  }
  return GRADE_LEVEL_MAP[raw.trim()] ?? (raw as GradeLevel);
}

function mapPublicClassToCourse(c: PublicClassItem): Course {
  const gradeLevel = mapApiGradeLevel(c.gradeLevel ?? c.gradeLevels?.[0]);
  const timeLabel = getScheduledTimeLabel(c) ?? '';
  const excerpt = buildClassExcerpt(c.excerpt, c.description);
  return {
    id: c.classId,
    name: c.title,
    time: timeLabel,
    gradeLevel,
    description: excerpt,
    fullDescription: c.description?.trim() ?? excerpt,
    priceLabel: formatClassPriceLabel(c.priceTotal),
    priceTotal: c.priceTotal,
    maxStudents: c.maxStudents,
    ageRange: c.ageRange,
    allowsFamilyDiscount: c.allowsFamilyDiscount,
    familyDiscountAmount: c.familyDiscountAmount,
    image: c.imageUrl,
  };
}

export interface LandingData {
  /** Null while loading or when no active semester exists. */
  activeSemester: ActiveSemester | null;
  courses: Course[];
  semesterLoading: boolean;
  coursesLoading: boolean;
  /** Unique teachers on approved, scheduled classes (from API). */
  teacherCount?: number;
  /** Count of approved classes for the active semester (from API). */
  classCount?: number;
  /** Average K–12 classes per grade for At a Glance (from API). */
  avgK12ClassesPerGrade?: number;
}

/**
 * Returns active semester and courses for the landing page from the public API.
 */
export function useLandingData(): LandingData {
  const cachedSemester = readPublicLandingSemesterCache();
  const [semesterPayload, setSemesterPayload] = useState(cachedSemester);
  const [courses, setCourses] = useState<Course[]>([]);
  // Always start loading — never show cached semester until the API confirms it.
  // This prevents stale session data from flashing before "no semester" appears.
  const [semesterLoading, setSemesterLoading] = useState(true);
  const [coursesLoading, setCoursesLoading] = useState(true);

  /** Loads the semester, then its classes; state is applied as each response arrives. */
  const loadLandingSemester = useCallback(
    (options?: { force?: boolean }): Promise<void> =>
      getActiveSemester(options).then(async (payload) => {
        setSemesterPayload(payload ?? null);
        if (payload) {
          writePublicLandingSemesterCache(payload);
          setSemesterLoading(false);
          setCoursesLoading(true);
          const list = await getPublicClasses(payload.semesterId);
          setCourses(list ? list.map(mapPublicClassToCourse) : []);
          setCoursesLoading(false);
          return;
        }
        clearPublicLandingSemesterCache();
        setCourses([]);
        setSemesterLoading(false);
        setCoursesLoading(false);
      }),
    []
  );

  // Both loading flags start true, so the initial load needs no synchronous state update.
  useEffect(() => {
    let cancelled = false;

    loadLandingSemester()
      .catch(() => {
        if (cancelled) return;
        setSemesterLoading(false);
        setCoursesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [loadLandingSemester]);

  useEffect(() => {
    if (semesterLoading || semesterPayload != null) {
      return;
    }

    const refetch = () => {
      if (document.visibilityState !== 'visible') return;
      void loadLandingSemester({ force: true });
    };

    const intervalId = window.setInterval(refetch, ACTIVE_SEMESTER_CACHE_MS_NONE);
    document.addEventListener('visibilitychange', refetch);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', refetch);
    };
  }, [semesterLoading, semesterPayload, loadLandingSemester]);

  useEffect(() => {
    const onChanged = () => {
      void loadLandingSemester({ force: true });
    };
    window.addEventListener(ACTIVE_SEMESTER_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(ACTIVE_SEMESTER_CHANGED_EVENT, onChanged);
  }, [loadLandingSemester]);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(ACTIVE_SEMESTER_BROADCAST_CHANNEL);
    channel.onmessage = (event: MessageEvent<{ type?: string }>) => {
      if (event.data?.type === 'active-semester-changed') {
        void loadLandingSemester({ force: true });
      }
    };
    return () => channel.close();
  }, [loadLandingSemester]);

  const activeSemester: ActiveSemester | null =
    semesterPayload != null
      ? buildActiveSemesterFromApi({
          name: semesterPayload.name,
          startDate: semesterPayload.startDate,
          endDate: semesterPayload.endDate,
          makeupDate: semesterPayload.makeupDate ?? null,
        })
      : null;

  return {
    activeSemester,
    courses,
    semesterLoading,
    coursesLoading,
    teacherCount: semesterPayload?.teacherCount,
    classCount: semesterPayload?.classCount,
    avgK12ClassesPerGrade: semesterPayload?.avgK12ClassesPerGrade,
  };
}
